import api from './api';
import { auth } from '../config/firebase';
import { getRecaptchaToken } from '../utils/recaptcha';
import { signInWithCustomToken, signOut as firebaseSignOut } from 'firebase/auth';
import type {
  User,
  Cliente,
  UserSession,
  LoginCredentials,
  RegisterData,
  PasswordChangeData,
} from '../types';

class AuthService {
  /**
   * Login with username and password
   * Returns user data and creates Firebase session
   */
  async login(credentials: LoginCredentials): Promise<UserSession> {
    console.log('[AuthService] Starting login...');
    const captchaToken = await getRecaptchaToken('login');
    const response = await api.post<{
      success: boolean;
      token?: string;
      user?: User;
      cliente?: Cliente;
      permissions?: string[];
      entidadeId?: string;
      requiresEmailUpdate?: boolean;
      requiresProfileUpdate?: boolean;
      error?: string;
    }>('/auth/login', credentials, {
      headers: captchaToken ? { 'x-recaptcha-token': captchaToken } : {},
    });

    console.log('[AuthService] API response:', response.data.success, response.data.user?.email);

    if (response.data.success && response.data.token) {
      try {
        console.log('[AuthService] Signing in to Firebase with custom token...');
        // Sign in to Firebase with custom token (exchanges for ID token)
        const userCredential = await signInWithCustomToken(auth, response.data.token);
        console.log('[AuthService] Firebase sign in successful, user:', userCredential.user.uid);

        // Get the actual ID token (this is what the API needs)
        const idToken = await userCredential.user.getIdToken();
        console.log('[AuthService] Got ID token, length:', idToken.length);

        const session: UserSession = {
          status: true,
          user: response.data.user,
          cliente: response.data.cliente,
          token: idToken, // Store the ID token, not the custom token
          requiresEmailUpdate: response.data.requiresEmailUpdate,
          requiresProfileUpdate: response.data.requiresProfileUpdate,
          permissions: response.data.permissions || [],
          entidadeId: response.data.entidadeId || undefined,
        };

        // Store session in sessionStorage
        sessionStorage.setItem('auth', JSON.stringify(session));
        console.log('[AuthService] Session stored, returning...');

        return session;
      } catch (firebaseError) {
        console.error('[AuthService] Firebase error:', firebaseError);
        throw new Error('Erro ao autenticar com Firebase: ' + (firebaseError as Error).message);
      }
    }

    throw new Error(response.data.error || 'Falha no login');
  }

  /**
   * Register new client account
   */
  async register(data: RegisterData): Promise<{ success: boolean; message: string }> {
    const captchaToken = await getRecaptchaToken('register');
    const response = await api.post<{
      success: boolean;
      message?: string;
      error?: string;
    }>('/auth/register', data, {
      headers: captchaToken ? { 'x-recaptcha-token': captchaToken } : {},
    });

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Conta criada com sucesso',
      };
    }

    throw new Error(response.data.error || 'Falha no registo');
  }

  /**
   * Verify current Firebase token
   */
  async verifyToken(): Promise<UserSession | null> {
    try {
      const user = auth.currentUser;
      if (!user) return null;

      const response = await api.post<{
        success: boolean;
        user?: User;
        cliente?: Cliente;
      }>('/auth/verify');

      if (response.data.success) {
        return {
          status: true,
          user: response.data.user,
          cliente: response.data.cliente,
        };
      }

      return null;
    } catch {
      return null;
    }
  }

  /**
   * Logout - clear Firebase session and local storage
   */
  async logout(): Promise<void> {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Firebase signout error:', error);
    } finally {
      sessionStorage.clear();
      localStorage.removeItem('auth');
    }
  }

  /**
   * Change password
   */
  async changePassword(data: PasswordChangeData, userId: string): Promise<void> {
    const response = await api.put<{ success: boolean; error?: string }>(
      `/client/profile/password`,
      {
        senhaAtual: data.senhaAtual,
        novaSenha: data.novaSenha,
        userId,
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao alterar senha');
    }
  }

  /**
   * Request password recovery
   */
  async recoverPassword(email: string): Promise<void> {
    const response = await api.post<{ success: boolean; error?: string }>(
      '/auth/reset-password',
      { email }
    );

    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao enviar email de recuperação');
    }
  }

  /**
   * Get current session from storage
   */
  getCurrentSession(): UserSession | null {
    try {
      const stored = sessionStorage.getItem('auth');
      if (stored) {
        return JSON.parse(stored) as UserSession;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const session = this.getCurrentSession();
    return session?.status === true && !!auth.currentUser;
  }

  /**
   * Update stored session
   */
  updateSession(updates: Partial<UserSession>): void {
    const current = this.getCurrentSession();
    if (current) {
      const updated = { ...current, ...updates };
      sessionStorage.setItem('auth', JSON.stringify(updated));
    }
  }
}

export default new AuthService();
